import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Shield, RotateCcw, Save, Lock } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Order matters — used as columns of the matrix.
const PERM_LABELS = {
  access_finance:           'Financeiro',
  access_budgets_landing:   'Orçamentos (Landing)',
  access_payments_page:     'Pagamentos',
  access_admin_panel:       'Painel Admin',
  access_activity_log:      'Log de Atividades',
  access_tools:             'Ferramentas',
  view_sensitive_community: 'Comunidade (dados sensíveis)',
  create_edit_projects:     'Criar/editar projetos',
  delete_projects:          'Excluir projetos',
  send_announcements:       'Enviar avisos',
};

const ROLE_LABELS = {
  administrador: 'Administrador',
  roteirista:    'Roteirista',
  mixador:       'Mixador',
  visitante:     'Visitante',
};

const RolePermissionsEditor = () => {
  const { user, getAuthHeaders, refreshPermissions } = useAuth();
  const [permKeys, setPermKeys] = useState([]);
  const [editableRoles, setEditableRoles] = useState([]);
  const [defaults, setDefaults] = useState({});
  const [current, setCurrent] = useState({});
  const [draft, setDraft] = useState({});
  const [savingRole, setSavingRole] = useState(null);

  const isDono = user?.role === 'dono';

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/role-permissions`, { headers: getAuthHeaders() });
      setPermKeys(res.data?.permission_keys || []);
      setEditableRoles(res.data?.editable_roles || []);
      setDefaults(res.data?.defaults || {});
      setCurrent(res.data?.current || {});
      // Initialize draft from current (only editable roles)
      const d = {};
      (res.data?.editable_roles || []).forEach(r => {
        d[r] = { ...(res.data?.current?.[r] || {}) };
      });
      setDraft(d);
    } catch {
      toast.error('Erro ao carregar permissões de cargos');
    }
  }, [getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const isModified = (role) => {
    if (!current[role] || !draft[role]) return false;
    return permKeys.some(k => !!current[role][k] !== !!draft[role][k]);
  };

  const toggle = (role, key) => {
    if (!isDono) return;
    setDraft(d => ({
      ...d,
      [role]: { ...(d[role] || {}), [key]: !d[role]?.[key] },
    }));
  };

  const save = async (role) => {
    setSavingRole(role);
    try {
      await axios.put(`${API}/role-permissions/${role}`,
        { permissions: draft[role] },
        { headers: getAuthHeaders() });
      toast.success(`Permissões de "${ROLE_LABELS[role] || role}" salvas!`);
      await load();
      // Refresh own permissions in context (in case dono is editing their own bypass-impacted UI)
      refreshPermissions?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar permissões');
    } finally {
      setSavingRole(null);
    }
  };

  const resetToDefault = async (role) => {
    setSavingRole(role);
    try {
      await axios.post(`${API}/role-permissions/${role}/reset`, {}, { headers: getAuthHeaders() });
      toast.success(`Permissões de "${ROLE_LABELS[role] || role}" restauradas ao padrão.`);
      await load();
      refreshPermissions?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao resetar permissões');
    } finally {
      setSavingRole(null);
    }
  };

  if (!editableRoles.length) {
    return null;
  }

  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden" data-testid="role-permissions-editor">
      <div className="p-4 border-b border-slate-700">
        <div className="flex items-center gap-3">
          <Shield className="h-5 w-5 text-amber-400" />
          <div>
            <h2 className="text-xl font-semibold text-white">Permissões por Cargo</h2>
            <p className="text-xs text-slate-400 mt-0.5">
              {isDono
                ? 'Marque o que cada cargo pode fazer. O Dono sempre tem acesso total e o Dublador, acesso limitado.'
                : 'Apenas o Dono pode alterar permissões.'}
            </p>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {editableRoles.map(role => (
          <div
            key={role}
            className="bg-slate-900/40 border border-slate-700 rounded-lg p-4"
            data-testid={`role-perm-card-${role}`}
          >
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <h3 className="text-base font-semibold text-white capitalize flex items-center gap-2">
                {ROLE_LABELS[role] || role}
                {!isDono && <Lock className="h-3.5 w-3.5 text-slate-500" />}
              </h3>
              <div className="flex items-center gap-2">
                {isDono && (
                  <>
                    <button
                      onClick={() => resetToDefault(role)}
                      disabled={savingRole === role}
                      className="flex items-center gap-1 text-xs text-slate-400 hover:text-amber-400 disabled:opacity-50 px-2 py-1 rounded"
                      data-testid={`role-perm-reset-${role}`}
                    >
                      <RotateCcw className="h-3 w-3" /> Padrão
                    </button>
                    <button
                      onClick={() => save(role)}
                      disabled={!isModified(role) || savingRole === role}
                      className="flex items-center gap-1 text-xs bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed text-white px-3 py-1.5 rounded-md font-medium"
                      data-testid={`role-perm-save-${role}`}
                    >
                      <Save className="h-3 w-3" /> {savingRole === role ? 'Salvando…' : 'Salvar'}
                    </button>
                  </>
                )}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {permKeys.map(pk => {
                const checked = !!draft[role]?.[pk];
                const changed = !!current[role]?.[pk] !== checked;
                return (
                  <label
                    key={pk}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer border ${
                      changed
                        ? 'border-amber-500/50 bg-amber-500/5'
                        : 'border-slate-700 bg-slate-800/40 hover:bg-slate-800/70'
                    } ${!isDono ? 'cursor-not-allowed opacity-80' : ''}`}
                    data-testid={`role-perm-${role}-${pk}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={!isDono}
                      onChange={() => toggle(role, pk)}
                      className="h-4 w-4 accent-emerald-500"
                      data-testid={`role-perm-checkbox-${role}-${pk}`}
                    />
                    <span className="text-sm text-slate-200">{PERM_LABELS[pk] || pk}</span>
                  </label>
                );
              })}
            </div>
          </div>
        ))}
        <p className="text-xs text-slate-500 italic pt-1">
          Padrões: {Object.entries(defaults).map(([r, p]) =>
            `${ROLE_LABELS[r] || r} (${Object.values(p || {}).filter(Boolean).length}/${permKeys.length})`
          ).join(' · ')}
        </p>
      </div>
    </div>
  );
};

export default RolePermissionsEditor;
